import { Badge } from '@shared/components/ui/badge';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { faPrint, faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UbicacionesIcon } from '@shared/components/ui/icons/warehouse.icons';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PackingStation, type PackingStationRelationsTabKey } from '../models/PackingStation';
import { DropdownActions } from '../components/interaction/DropdownActions';

interface UseAgGridColumnsProps {
  selectedColumns?: string[];
  onOpenDetail?: (station: PackingStation, tab?: PackingStationRelationsTabKey) => void;
  onOpenEdit?: (station: PackingStation) => void;
  onPrint?: (station: PackingStation, size: 'small' | 'large') => void;
  refetch?: () => void;
}

// Convierte columnas AG Grid a ColumnDef de TanStack para ColumnSelector
const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<PackingStation>[] => {
  return agGridColumns
    .filter((col) => {
      const id = col.colId || col.field || col.headerName || 'unknown';
      return id !== 'actions' && id !== 'unknown';
    })
    .map((col) => ({
      id: col.colId || col.field || col.headerName || 'unknown',
      accessorKey: col.field || col.headerName || 'unknown',
      header: col.headerName || 'Columna',
    }));
};

const pickNumber = (source: Record<string, any>, keys: string[]): number | undefined => {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
};

export const useAgGridColumns = ({
  selectedColumns = [],
  onOpenDetail,
  onOpenEdit,
  onPrint,
  refetch,
}: UseAgGridColumnsProps) => {
  const { t } = useTranslation('packingStationsModule');

  // Definiciones completas de columnas para AG Grid
  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // COLUMNA: Código del puesto
      {
        field: 'code',
        headerName: t('table.columns.code'),
        minWidth: 120,
        flex: 1,
        cellRenderer: (params: any) => {
          const station = params.data as PackingStation;
          return <div className="text-sm font-medium text-title font-mono">{station.code || '-'}</div>;
        },
      },

      // COLUMNA: Estado activo/inactivo
      {
        field: 'active',
        headerName: t('table.columns.status'),
        width: 110,
        minWidth: 110,
        flex: 1,
        cellRenderer: (params: any) => {
          const station = params.data as PackingStation;
          return station.active ? (
            <Badge variant="default" className="text-xs bg-bg-success text-success border-0">
              {t('table.status.active')}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-title">
              {t('table.status.inactive')}
            </Badge>
          );
        },
      },

      // COLUMNA: Impresoras (muestra contador clickeable)
      {
        field: 'printers',
        headerName: t('table.columns.printers'),
        width: 120,
        minWidth: 120,
        flex: 1,
        cellRenderer: (params: any) => {
          const station = params.data as PackingStation;
          const rawStation = station as Record<string, any>;
          const printers = Array.isArray(rawStation.printers) ? rawStation.printers : [];
          const printersCount = pickNumber(rawStation, ['printers_count', 'printersCount']) ?? printers.length;

          const printers10x15Count =
            pickNumber(rawStation, ['printers_10x15_count', 'printers10x15Count', 'printers_type_10x15_count']) ??
            printers.filter((printer: any) => printer?.type === '10x15').length;

          const printersA4Count =
            pickNumber(rawStation, ['printers_a4_count', 'printersA4Count', 'printers_type_a4_count']) ??
            printers.filter((printer: any) => printer?.type === 'a4').length;

          return (
            <button
              className="text-sm text-title hover:text-primary transition-colors cursor-pointer flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetail?.(station, 'printers');
              }}
              data-testid="open-printers-detail"
            >
              <FontAwesomeIcon icon={faPrint} className="w-3.5 h-3.5 text-icons" />
              <span className="text-sm">{printersCount}</span>
              <span className="text-xs text-subtext">
                {t('table.printersByType', {
                  count10x15: printers10x15Count,
                  countA4: printersA4Count,
                })}
              </span>
            </button>
          );
        },
      },

      // COLUMNA: Clientes (muestra contador clickeable)
      {
        field: 'clients',
        headerName: t('table.columns.clients'),
        width: 120,
        minWidth: 120,
        flex: 1,
        cellRenderer: (params: any) => {
          const station = params.data as PackingStation;
          const rawStation = station as Record<string, any>;
          const clients = Array.isArray(rawStation.clients) ? rawStation.clients : [];
          const clientsCount = pickNumber(rawStation, ['clients_count', 'clientsCount']) ?? clients.length;

          return (
            <button
              className="text-sm text-title hover:text-primary transition-colors cursor-pointer flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetail?.(station, 'clients');
              }}
              data-testid="open-clients-detail"
            >
              <FontAwesomeIcon icon={faUser} className="w-3.5 h-3.5 text-icons" />
              <span className="text-sm">{clientsCount}</span>
            </button>
          );
        },
      },

      // COLUMNA: Ubicaciones (muestra contador clickeable)
      {
        field: 'locations',
        headerName: t('table.columns.locations'),
        width: 120,
        minWidth: 120,
        flex: 1,
        cellRenderer: (params: any) => {
          const station = params.data as PackingStation;
          const rawStation = station as Record<string, any>;
          const locations = Array.isArray(rawStation.locations) ? rawStation.locations : [];
          const locationsCount = pickNumber(rawStation, ['locations_count', 'locationsCount']) ?? locations.length;

          const locationItemsCount =
            pickNumber(rawStation, [
              'location_items_count',
              'locationItemsCount',
              'locations_items_count',
              'locationsItemsCount',
              'items_count',
              'itemsCount',
            ]) ?? 0;

          return (
            <button
              className="text-sm text-title hover:text-primary transition-colors cursor-pointer flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetail?.(station, 'locations');
              }}
              data-testid="open-locations-detail"
            >
              <UbicacionesIcon className="w-3.5 h-3.5 text-icons" />
              <span className="text-sm">{locationsCount}</span>
              <span className="text-xs text-subtext">({locationItemsCount} items)</span>
            </button>
          );
        },
      },
    ];

    // COLUMNA: Acciones (dropdown)
    // valueGetter rastrea `active` para que AG Grid refresque la celda
    // cuando se cambia el estado del puesto y el texto del dropdown
    // (Activar/Desactivar) refleje el valor actualizado.
    baseColumns.push({
      headerName: '',
      colId: 'actions',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right',
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      valueGetter: (params: any) => params.data?.active,
      cellRenderer: (params: any) => {
        const station = params.data as PackingStation;
        return (
          <div className="flex justify-end w-full">
            <DropdownActions
              station={station}
              onOpenDetail={onOpenDetail}
              onOpenEdit={onOpenEdit}
              onPrint={onPrint}
              refetch={refetch}
            />
          </div>
        );
      },
    });

    return baseColumns;
  }, [t, onOpenDetail, onOpenEdit, onPrint, refetch]);

  // Filtrar columnas basado en la selección del ColumnSelector
  const filteredColumnDefs: ColDef[] = useMemo(() => {
    const dedupeByField = (cols: ColDef[]) => {
      const seen = new Set<string>();
      const result: ColDef[] = [];
      cols.forEach((c) => {
        const id = (c.colId || c.field || c.headerName || 'unknown') as string;
        if (!seen.has(id)) {
          seen.add(id);
          result.push(c);
        }
      });
      return result;
    };

    if (selectedColumns.length === 0) {
      return dedupeByField(allColumnDefs);
    }

    const filtered = allColumnDefs.filter((col) => {
      const fieldId = col.colId || col.field || col.headerName || 'unknown';
      if (fieldId === 'actions') return true;
      return selectedColumns.includes(fieldId);
    });

    return filtered;
  }, [allColumnDefs, selectedColumns]);

  // Columnas para el ColumnSelector (formato TanStack)
  const tanStackColumns: ColumnDef<PackingStation>[] = useMemo(() => {
    return convertToTanStackColumns(allColumnDefs);
  }, [allColumnDefs]);

  return {
    allColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
